import React, { useEffect } from "react";
import styled from "styled-components";
import numeral from "numeral";
import renderIf from "render-if";
import formatDate from "date-fns/lightFormat";
import addHours from "date-fns/addHours";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import KeyIcon from "@material-ui/icons/VpnKey";
import ServiceRecordsIcon from "@material-ui/icons/Build";
import TitleIcon from "@material-ui/icons/Book";
import BankIcon from "@material-ui/icons/AccountBalance";
import { withTheme } from "@material-ui/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useParams } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import { CallToActionButton } from "../components/Buttons";

import useYMMTLabels from "../hooks/useYMMTLabels";
import formatVehicleTitle from "../utils/format-vehicle-title";
import formatCondition from "../utils/format-condition";
import useAccountSettings from "../hooks/useAccountSettings";
import Padded from "../layout/Padded";
import Page from "../layout/Page";
import DealerCash from "../components/DealerCash";
import BellCurveGraph from "../components/BellCurveGraph";

import useTradeInState from "../hooks/useTradeInState";
import { fireCompleteEvent } from "../utils/gtm-events";

const Content = styled(Padded)`
  width: 100%;
  max-width: 750px;
  margin: 0 auto;
`;

const PaddedDivider = styled(Divider)`
  && {
    margin: 20px 0;
  }
`;

const Graph = styled(BellCurveGraph)`
  margin: 0 auto;
`;

const ReportPage = ({ children, theme }) => {
  const params = useParams();
  const { accountId } = params;
  const [tradeIn] = useTradeInState();
  const [{ tradeInDealerCash, accountName, removeBranding, callToAction }] =
    useAccountSettings(params);
  const [[year, make, model, trim]] = useYMMTLabels(params);
  const vehicleTitle = formatVehicleTitle({ year, make, model });

  const { tradeInValues, miles, condition, date = Date.now() } = tradeIn;
  const tradeInValue = tradeInValues[condition];

  const formattedTradeInValue = numeral(tradeInValue).format("$0,0");
  const formattedMiles = numeral(miles).format("0,0");
  const formattedCondition = formatCondition(condition);
  const expirationDate = addHours(new Date(parseInt(date)), 48);

  useEffect(() => {
    if (year && make && model && trim) {
      fireCompleteEvent({ leadId: tradeIn.leadId, year, make, model, trim });
    }
  }, [year, make, model, trim, tradeIn.leadId]);

  const header = (
    <PageHeader
      title={`${formattedTradeInValue} Trade In Value`}
      subtitle={`for your ${vehicleTitle}`}
      link={`/${accountId}`}
      removeBranding={removeBranding}
    />
  );

  const smallDevice = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Page header={header} footer={children}>
      <Content>
        <Graph values={tradeInValues} width={smallDevice ? 350 : 500} />
        <PaddedDivider />
        <Typography variant="body1" color="textPrimary">
          Based on recent transactions, {accountName} has estimated that your
          trade in of a {vehicleTitle} with {formattedMiles} miles could be
          worth up to {formattedTradeInValue}. This trade in value is an
          estimate, as it assumes the vehicle is in{" "}
          {formattedCondition.toLowerCase()} physical and mechanical condition
          with no accidents or concerns from the vehicle history report. To
          receive an exact appraisal of the value of your vehicle, please
          contact us to schedule an appraisal at our dealership.
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Due to ever changing market conditions, this report is valid for 48
          hours and is set to expire at {formatDate(expirationDate, "h aaaa")}{" "}
          on {formatDate(expirationDate, "M/d/yyyy")}.
        </Typography>
        {renderIf(callToAction)(
          callToAction && callToAction.enabled && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 30,
                marginTop: 10,
              }}
            >
              <a href={callToAction.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none'}}>
                <CallToActionButton>
                  {callToAction.label}
                </CallToActionButton>
              </a>
            </div>
          )
        )}
        {renderIf(tradeInDealerCash)(
          <>
            <PaddedDivider />
            <DealerCash
              name={accountName}
              amount={tradeInDealerCash}
              vehicle={vehicleTitle}
            />
          </>
        )}
        <PaddedDivider />
        <Typography variant="h3" color="primary">
          Things to Remember When Trading-In Your Vehicle
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <KeyIcon />
            </ListItemIcon>
            <ListItemText>
              Bring both set of keys if you have them.
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ServiceRecordsIcon />
            </ListItemIcon>
            <ListItemText>Bring any vehicle service records.</ListItemText>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <BankIcon />
            </ListItemIcon>
            <ListItemText>
              If your current vehicle is financed, be sure to bring all loan
              documentation.
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TitleIcon />
            </ListItemIcon>
            <ListItemText>
              If you have the title, please bring it with you.
            </ListItemText>
          </ListItem>
        </List>
        <Typography variant="subtitle2" color="textSecondary">
          Every attempt is made to ensure the accuracy of this PureCars Trade In
          Report. However, the accuracy of this report is not guaranteed by
          PureCars or the dealership. This is not an offer to purchase your
          vehicle. The trade values shown within the PureCars Trade In Report is
          based on the condition and other details which were entered and may be
          adjusted higher or lower depending on the vehicles actual condition,
          options and other value factoring attributes. The actual trade value
          may vary depending on the dealers actual appraisal. PureCars Trade In
          Report is not an independent service. This dealership subscribes to
          the PureCars Trade In service and has input in details with the
          report. The PureCars Trade In value is a non-binding estimate. A
          formal appraisal must be completed at the dealership for an actual
          trade in value.
        </Typography>
      </Content>
    </Page>
  );
};

export default withTheme(ReportPage);
